import { queryFilter } from 'utils/proptypes';
import base from './baseApi';
import { createSearchParams } from 'react-router';
import { omitBy } from 'lodash';
import { getHostedFormAccentColor } from 'utils/color.util';

const { urls, methods, execute, get, post, del, patch } = base();

const getUsers = async (queryFilter: queryFilter & { isBooking?: boolean }) => {
  const method = methods.GET;
  const initParams: any = omitBy(queryFilter, v => v === '');
  const params = createSearchParams(initParams);

  const url = `${urls.user.getUsers}?${params.toString()}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const getSiteUsers = async (queryFilter: queryFilter) => {
  const method = methods.GET;
  const initParams: any = omitBy(queryFilter, v => v === '');
  const params = createSearchParams(initParams);

  const url = `${urls.user.siteUsers}?${params.toString()}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const getCurrentUser = () => {
  const method = methods.GET;
  const url = `${urls.user.getCurrentUser}`;
  const response = execute(method, url, { authorization: true });

  return response;
};

const updateUserProfile = (payload: any) => {
  const method = methods.PUT;
  const url = `${urls.user.userProfile}`;
  const response = execute(method, url, { authorization: true }, payload);

  return response;
};

const updateSiteUser = ({ id, payload }: any) => {
  const method = methods.PUT;
  const url = `${urls.user.siteUsers}/${id}`;
  const response = execute(method, url, { authorization: true }, payload);

  return response;
};

const createSiteUser = ({ payload }: any) => {
  const method = methods.POST;
  const url = urls.user.siteUsers;

  const response = execute(method, url, { authorization: true }, payload);
  return response;
};

const changeEmailAddress = (id: string, emailAddress: string) => {
  const method = methods.GET;
  const url = `${urls.user.setRoles}/?id=${id}&emailAddress=${emailAddress}`;
  const response = execute(method, url, { authorization: true });

  return response;
};

const updateActiveUserById = (payload: { id: string, isActive: boolean }) => {
  const method = methods.PATCH;
  const url = `${urls.user.users}/${payload.id}`;
  const response = execute(method, url, { authorization: true }, { isActive: payload.isActive });

  return response;
};

const setRolesForUser = (payload: { userId: string, roleIds: string[] }) => {
  const method = methods.GET;
  const url = urls.user.setRoles;
  const response = execute(method, url, { authorization: true }, payload);
  return response;
};

const updateUserImage = (payload: { id: string, file?: any }) => {
  const { id, file } = payload;
  const method = methods.POST;
  const url = `${urls.user.users}/${id}/uploadimage`;
  const response = execute(method, url, { authorization: true }, { file }, true);
  return response;
};

const confirmAndSaveProfileImage = (payload: { userId: string, fileName?: string }) => {
  const method = methods.PUT;
  const url = `${urls.user.users}/confirmAndSaveFile`;
  const response = execute(method, url, { authorization: true }, payload);
  return response;
};

const getUsersByEmail = async (emailAddress: string) => {
  const method = methods.GET;
  const url = `${urls.user.userEmail}?emailAddress=${emailAddress}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const getUserById = async (id: string) => {
  const method = methods.GET;
  const url = `${urls.user.users}/${id}`;
  const response = await execute(method, url, { authorization: true });
  return response;
};

const assignRoles = ({ id, payload }: any) => {
  const method = methods.PUT;
  const url = `${urls.user.users}/${id}/roles`;
  return execute(method, url, { authorization: true }, payload);
};

const getUserCards = () => {
  const method = methods.GET;
  const url = `${urls.user.cards}`;
  const response = execute(method, url, { authorization: true });

  return response;
};

/**
 * Body both hosted-page token calls send. `accentColor` is the live theme primary — the API
 * forwards it as Authorize.net's hostedProfileHeadingBgColor so the hosted form's field lines
 * and Save button pick up the site/org brand instead of stock grey. Omitted when the theme var
 * isn't a plain hex; the API then leaves the setting off entirely.
 */
const hostedPageTokenBody = () => {
  const accentColor = getHostedFormAccentColor();
  return accentColor ? { siteOrigin: window.location.origin, accentColor } : { siteOrigin: window.location.origin };
};

/**
 * Accept Customer hosted profile page (SAQ A): fetch a single-use page token — card entry
 * itself happens on Authorize.net's page inside an iframe.
 */
const getHostedProfilePageToken = (recaptchaToken?: string | null, isCheckbox = false) => {
  const url = `${urls.user.hostedProfilePageToken}`;
  const headers: Record<string, any> = {};
  if (recaptchaToken) {
    headers['x-recaptcha-token'] = recaptchaToken;
    if (isCheckbox) headers['x-recaptcha-checkbox'] = 'true';
  }
  return post(url, hostedPageTokenBody(), headers);
};

/**
 * Hosted editPayment page token for an existing saved card (fix a wrong zip/expiry/PAN).
 * The response's paymentProfileId must be POSTed to the editPayment URL alongside the token.
 */
const getHostedCardEditPageToken = (cardId: string, recaptchaToken?: string | null, isCheckbox = false) => {
  const url = `${urls.user.hostedCardEditPageToken.replace(':cardId', cardId)}`;
  const headers: Record<string, any> = {};
  if (recaptchaToken) {
    headers['x-recaptcha-token'] = recaptchaToken;
    if (isCheckbox) headers['x-recaptcha-checkbox'] = 'true';
  }
  return post(url, hostedPageTokenBody(), headers);
};

/** After the hosted profile page saves a card, pull the new profile into user_card rows. */
const syncUserCards = () => {
  const url = `${urls.user.syncCards}`;
  return post(url, {}, {});
};

const setDefaultUserCard = (cardId: any) => {
  const url = `${urls.user.setDefaultCard.replace(':cardId', cardId)}`;
  return get(url);
};

const deleteUserCard = (cardId: any) => {
  const url = `${urls.user.deleteCard.replace(':cardId', cardId)}`;
  return del(url);
};

/** Correct the cardholder name on a saved card — the hosted pages have no name field. Send null to clear it back to unknown. */
const updateUserCardName = (cardId: string, cardHolderName: string | null) => {
  const url = `${urls.user.updateCard.replace(':cardId', cardId)}`;
  return patch(url, { cardHolderName });
};

export {
  assignRoles,
  changeEmailAddress,
  confirmAndSaveProfileImage,
  createSiteUser,
  getCurrentUser,
  getSiteUsers,
  getUserById,
  getUsers,
  getUsersByEmail,
  setRolesForUser,
  updateActiveUserById,
  updateSiteUser,
  updateUserImage,
  updateUserProfile,
  getUserCards,
  getHostedProfilePageToken,
  getHostedCardEditPageToken,
  syncUserCards,
  setDefaultUserCard,
  deleteUserCard,
  updateUserCardName,
};
